import axios from 'axios';
import { FeatureBase } from './base/FeatureBase';
import { getResourceString } from './../../../kentico/resource-strings';

export class AdresZoeker extends FeatureBase {

  private formData = {};
  private setFormData: any;
  private onlyDigitsPattern = /^\d+$/;
  private partialZipCodePattern = /^\d{0,4}$|^\d{4}[A-Za-z]{0,2}$/;
  private fullZipCodePattern = /^\d{4}[A-Z]{2}$/;

  public fieldNames: string[] = [
    "Land", 
    "Postcode", 
    "Huisnummer", 
    "Toevoeging", 
    "Straat", 
    "Woonplaats",
    "PostcodeAutomatischInvullen",
    "PostcodeHandmatigInvullen"
  ]

  static delayAdresZoeker = null;

  constructor(formData: any = null, setFormData: any = null) {
    super();
    this.formData = formData;
    this.setFormData = setFormData;
  }

  public initFeatureField(field: Field, formDataEntry: FormDataEntry, fields: Field[]): FormDataEntry {
    const fieldName = field["@column"];

    if(fieldName.endsWith("Land")) {
      return formDataEntry;
    }

    const prefix = this.getPrefixFromFieldName(fieldName);

    const countryField: Field = fields.find((field) => field["@column"] == `${prefix}Land`);
    if(countryField) {
      const countryValue = countryField.properties?.defaultvalue || ''
      if(fieldName.endsWith("Straat") || fieldName.endsWith("Woonplaats")) {
        formDataEntry.enabled = !(countryValue == "NL")
      }
      if(fieldName.endsWith("PostcodeAutomatischInvullen")) {
        formDataEntry.enabled = countryValue == "NL";
      }
      if(fieldName.endsWith("PostcodeHandmatigInvullen")) {
        formDataEntry.enabled = false;
      }
    }
    
    return formDataEntry;
  }

  public validateField(name: string, value: any): string {
    const prefix = this.getPrefixFromFieldName(name);
    const country = this.formData[`${prefix}Land`].value;

    switch (name) {
      case (name.endsWith("Postcode") ? "Postcode" : ""):
        if(country == "NL") {
          return this.fullZipCodePattern.test(value) ? null : getResourceString("Form.Postalcode.Error");
        }
        return null;
      case (name.endsWith("Huisnummer") ? "Huisnummer" : ""):
        return this.onlyDigitsPattern.test(value)  ? null : getResourceString("Form.Housenumber.ErrorOnlyNumbers");
      default:
        return null;
    }
  }

  public handleAdresZoekerChange = async (e) => {
    const { name, value } = e.target;
    await this.setAdresZoeker(name, value);
  }

  public getStreetAndCity = async (zipCode, houseNumber, houseNumberExt) => {
    try {
      const response = await axios.get(`/api/esb/getaddress?postcode=${zipCode}&huisnummer=${houseNumber}${houseNumberExt != null ? `&huisnummertoevoeging=${houseNumberExt}` : ``}`);
      const place = response.data;

      if(!place || Object.keys(place).length == 0) {
        return null;
      }

      return {
        street: place['streetField'],
        city: place['cityField']
      };
    } catch (error) {
      throw new Error('Invalid ZIP code or country code.');
    }
  }

  public setAdresZoeker = async (name: string, value: string) => {
    const formDataTemp = Object.assign({}, this.formData);
    
    let adresInputsValid = false;

    const prefix = this.getPrefixFromFieldName(name);
    const country = formDataTemp[`${prefix}Land`].value;

    switch (name) {
      case (name.endsWith("Land") ? "Land" : ""):
        formDataTemp[name].value = value;
        formDataTemp[`${prefix}Straat`] = {value: "", touched: false, focus: false, enabled: value != "NL", loading: false};
        formDataTemp[`${prefix}Woonplaats`] = {value: "", touched: false, focus: false, enabled: value != "NL", loading: false};
        formDataTemp[`${prefix}Postcode`] = {value: "", touched: false, focus: false, enabled: true, loading: false};
        formDataTemp[`${prefix}Huisnummer`] = {value: "", touched: false, focus: false, enabled: true, loading: false};
        formDataTemp[`${prefix}Toevoeging`] = {value: "", touched: false, focus: false, enabled: true, loading: false};
        formDataTemp[`${prefix}PostcodeHandmatigInvullen`] = { enabled: false };
        formDataTemp[`${prefix}PostcodeAutomatischInvullen`] = { enabled: value == "NL" };
        break;
      case (name.endsWith("Postcode") ? "Postcode" : ""):
        if(value != null) {
          value = value.replace(/\s+/g, '');
        }

        if(value == null || value == "" || this.partialZipCodePattern.test(value) || this.fullZipCodePattern.test(value) || country != "NL") {
          value = country == "NL" ? value.toUpperCase() : value;
          formDataTemp[name] = {value: value, touched: true, focus: true, enabled: true};
        }
        break;
      case (name.endsWith("Huisnummer") ? "Huisnummer" : ""):
        if(this.onlyDigitsPattern.test(value) || value == '') {
          formDataTemp[name] = {value: value, touched: true, focus: true, enabled: true, loading: false};
        }
        break;
      case (name.endsWith("Straat") || name.endsWith("Woonplaats") || name.endsWith("Toevoeging") ? name : ""):
        formDataTemp[name] = {value: value, touched: true, focus: true, enabled: true, loading: false};
        break;
      default:
        break;
    }

    adresInputsValid = 
      this.fullZipCodePattern.test(formDataTemp[`${prefix}Postcode`].value) && 
      this.onlyDigitsPattern.test(formDataTemp[`${prefix}Huisnummer`].value) && 
      formDataTemp[`${prefix}Land`].value == "NL" && 
      name != `${prefix}Straat` && 
      name != `${prefix}Woonplaats`;

    if(adresInputsValid) {
      formDataTemp[`${prefix}Straat`].loading = true;
      formDataTemp[`${prefix}Straat`].value = '';
      formDataTemp[`${prefix}Woonplaats`].loading = true;
      formDataTemp[`${prefix}Woonplaats`].value = '';
    }

    this.setFormData(formDataTemp);

    if (AdresZoeker.delayAdresZoeker) {
      clearTimeout(AdresZoeker.delayAdresZoeker);
      AdresZoeker.delayAdresZoeker = null;
    }

    if(adresInputsValid) {
      AdresZoeker.delayAdresZoeker = setTimeout(async () => {
        // Fetch data
        var data = await this.getStreetAndCity(
          formDataTemp[`${prefix}Postcode`].value,
          formDataTemp[`${prefix}Huisnummer`].value,
          formDataTemp[`${prefix}Toevoeging`].value
        );

        // Validate data
        if (!data.street || !data.city) {
          data = null;
        }

        // Update form data based on the fetched data
        this.setFormData((prevFormData) => {
          const newFormData = {
            ...prevFormData,
            [`${prefix}PostcodeHandmatigInvullen`]: { enabled: data ? false : true },
            [`${prefix}PostcodeAutomatischInvullen`]: { enabled: !data ? false : true },
            [`${prefix}Straat`]: { value: data ? data.street : '', focus: formDataTemp[`${prefix}Straat`].focus, enabled: data ? false : true, touched: false, loading: false },
            [`${prefix}Woonplaats`]: { value: data ? data.city : '', focus: formDataTemp[`${prefix}Woonplaats`].focus, enabled: data ? false : true, touched: false, loading: false },
          };
    
          return newFormData;
        });
      }, 1500);
    }
  }
}